import React, { useState, useMemo } from "react";
import {  Button, Form } from "react-bootstrap";
import { formatPrice } from "utils/utils";
import ReservationIncomeForm from "./ReservationIncomeForm";
import { IconPlus, IconEdit, IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { postRequest, deleteRequest } from "services/api";
import Swal from "sweetalert2";
import BookingDetailModal from "./BookingDetailModal";

const PaymentMethodAccordionAction = ({
  dataPerPaymentMethod,
  calculateNetKuber,
  onRefreshData,
  allPaymentMethods = [],
}) => {
  const [showReservationIncomeModal, setShowReservationIncomeModal] =
    useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentInputs, setPaymentInputs] = useState({}); // {bookingCode: {paymentMethod: '', amount: ''}}
  const [isSubmitting, setIsSubmitting] = useState({}); // {bookingCode: boolean}
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);
  const [selectedBookings, setSelectedBookings] = useState([]);

  const handleCreateReservationIncome = (booking) => {
    setSelectedBooking(booking);
    setShowReservationIncomeModal(true);
  };

  const handleUpdateReservationIncome = (booking) => {
    setSelectedBooking(booking);
    setShowReservationIncomeModal(true);
  };

  const handleCloseReservationIncomeModal = () => {
    setShowReservationIncomeModal(false);
    setSelectedBooking(null);
  };

  const handleShowDetail = (booking) => {
    setDetailBooking(booking);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setDetailBooking(null);
  };

  const handlePaymentInputChange = (bookingCode, field, value) => {
    setPaymentInputs((prev) => ({
      ...prev,
      [bookingCode]: {
        ...prev[bookingCode],
        [field]: value,
      },
    }));
  };

  const handlePaymentMethodSelect = (booking, paymentMethodId) => {
    const bookingCode = booking.code;
    
    // Calculate remaining balance
    const totalBill = Number(booking.payment_bill) || 0;
    const paidAmount = booking.reservation_income?.payment_items?.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    ) || 0;
    const remainingBalance = Math.max(0, totalBill - paidAmount);

    setPaymentInputs((prev) => ({
      ...prev,
      [bookingCode]: {
        ...prev[bookingCode],
        payment_method_id: paymentMethodId,
        amount: remainingBalance > 0 ? remainingBalance.toString() : "",
      },
    }));

    // Auto-focus the amount input
    setTimeout(() => {
      const amountInput = document.getElementById(`amount-input-${bookingCode}`);
      if (amountInput) amountInput.focus();
    }, 0);
  };

  const handleAddPaymentMethod = async (booking) => {
    const bookingCode = booking.code;
    const input = paymentInputs[bookingCode] || {};
    
    if (!input.payment_method_id || !input.amount) {
      alert("Please select a payment method and enter an amount");
      return;
    }

    if (!booking.reservation_income_id) {
      alert("Please create reservation income first");
      return;
    }

    setIsSubmitting(prev => ({ ...prev, [bookingCode]: true }));

    try {
      const payload = {
        reservation_income_id: booking.reservation_income_id,
        payment_method_id: input.payment_method_id,
        amount: Number(input.amount),
      };

      const result = await postRequest("/reservation-payment-v2", payload);

      if (result.success) {
        // Clear input
        setPaymentInputs((prev) => ({
          ...prev,
          [bookingCode]: { payment_method_id: "", amount: "" },
        }));
        
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Payment added successfully",
          showConfirmButton: false,
          timer: 1500,
        });

        // Refresh data
        if (onRefreshData) onRefreshData();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.message || "Failed to add payment method",
        });
      }
    } catch (error) {
      console.error("Error adding payment method:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "An error occurred",
      });
    } finally {
      setIsSubmitting(prev => ({ ...prev, [bookingCode]: false }));
    }
  };

  const handleRemovePaymentMethod = async (paymentId, bookingCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    setIsSubmitting(prev => ({ ...prev, [bookingCode]: true }));

    try {
      const response = await deleteRequest(`/reservation-payment-v2/${paymentId}`);
      if (response.success) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Payment removed successfully",
          showConfirmButton: false,
          timer: 1500,
        });
        if (onRefreshData) onRefreshData();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "Failed to remove payment method",
        });
      }
    } catch (error) {
      console.error("Error removing payment method:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "An error occurred",
      });
    } finally {
      setIsSubmitting(prev => ({ ...prev, [bookingCode]: false }));
    }
  };

  // Flatten all bookings from all payment methods
  const allBookings = useMemo(() => {
    const bookings = [];
    Object.entries(dataPerPaymentMethod).forEach(
      ([paymentMethodName, paymentData]) => {
        paymentData.data.forEach((booking) => {
          bookings.push({ ...booking, paymentMethodName });
        });
      }
    );
    return bookings;
  }, [dataPerPaymentMethod]);

  // Calculate split bills map
  const splitBillsMap = useMemo(() => {
    const map = {};
    Object.entries(dataPerPaymentMethod).forEach(
      ([paymentMethodName, paymentData]) => {
        paymentData.data.forEach((booking) => {
          const code = booking.code;
          if (!map[code]) map[code] = [];
          map[code].push({
            paymentMethodName,
            amount: Number(booking.payment_bill) || 0,
          });
        });
      }
    );
    return map;
  }, [dataPerPaymentMethod]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = [...new Set(allBookings.map(b => b.id))];
      setSelectedBookings(allIds);
    } else {
      setSelectedBookings([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedBookings(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSendToBackup = async () => {
    if (selectedBookings.length === 0) {
      Swal.fire("Peringatan", "Pilih minimal 1 data untuk di-backup", "warning");
      return;
    }

    const result = await Swal.fire({
      title: "Kirim ke Backup",
      text: `Akan mengirim ${selectedBookings.length} data ke server backup. Lanjutkan? (Fitur API Backend belum siap)`,
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Ya, Kirim",
      cancelButtonText: "Batal"
    });

    if (result.isConfirmed) {
      console.log("Payload to BE:", { reservation_ids: selectedBookings });
      // TODO: Call API endpoint POST /api/v1/accounting-backup/send
      Swal.fire("Berhasil", "Permintaan pengiriman data sedang disimulasikan.", "success");
      setSelectedBookings([]);
    }
  };

  return (
    <>
      <div className="mb-4 bg-light p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">All Bookings</h5>
          {selectedBookings.length > 0 && (
            <Button variant="primary" size="sm" onClick={handleSendToBackup}>
              Send to Backup ({selectedBookings.length})
            </Button>
          )}
        </div>
        <div className="table-responsive bg-white">
          <table className="table table-bordered table-hover table-sm table-vcenter">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: '40px' }}>
                  <Form.Check 
                    type="checkbox"
                    checked={
                      selectedBookings.length > 0 && 
                      selectedBookings.length === new Set(allBookings.map(b => b.id)).size
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Tgl</th>
                <th>Transaksi ATV</th>
                <th className="text-end">Payment Bill</th>
                <th className="text-end">Net Agent</th>
                <th className="text-end">Susuk Agent</th>
                <th className="text-end">Net Kuber</th>
                <th className="text-center">ATV</th>
                <th className="text-end">Others</th>
                <th className="text-end">Type</th>
                <th className="text-end">Deduction</th>
                <th className="text-end">NET AGENT</th>
                <th>note</th>
                <th>Add Payment Method</th>
              </tr>
            </thead>
            <tbody>
              {allBookings.map((booking, index) => {
                // Menghitung others berdasarkan total_atv
                const totalMotor = Number(booking.total_atv) || 0;
                const othersValue = totalMotor * 50000;

                const netKuber = calculateNetKuber(booking);

                return (
                  <tr key={index}>
                    <td className="text-center align-middle">
                      <Form.Check
                        type="checkbox"
                        checked={selectedBookings.includes(booking.id)}
                        onChange={() => handleSelect(booking.id)}
                      />
                    </td>
                    <td>
                      <small>{booking.reservation_date}</small>
                      <br />
                      <strong>{booking.code}</strong>
                      <br />
                      <IconInfoCircle 
                        size={14} 
                        className="ms-1 cursor-pointer text-info" 
                        onClick={() => handleShowDetail(booking)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td>
                      <div className="small">
                        <strong>Activity:</strong>
                        {booking.activities?.map((a, i) => (
                          <div key={i}>
                            ({a.qty}x) {a.activity}:{" "}
                            {formatPrice(Number(a.price) || 0)}
                          </div>
                        )) || <div>No activities</div>}
                        <div>
                          <strong>Transport:</strong>{" "}
                          {booking.guest_transport || "-"}
                        </div>
                        <div>
                          <strong>Driver:</strong> {booking.driver || "-"}
                        </div>
                      <span className="text-primary">Guest: {booking.guest_name}</span>

                        <div>
                          <strong>Hotel:</strong> {booking.hotel || "-"}
                        </div>
                        <div>
                          <strong>Agent:</strong>{" "}
                          <span>{booking.agent_name || "-"}</span>
                        </div>
                        <div>
                          <strong>Payment:</strong>{" "}
                          {booking.paymentMethodName || "-"}
                        </div>
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="text-end border-0">
                        <strong>
                          {formatPrice(Number(booking.payment_bill) || 0)}
                        </strong>
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="text-end border-0">
                        <strong>
                          {formatPrice(Number(booking.net_agent) || 0)}
                        </strong>
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="text-end border-0">
                        <strong>
                          {formatPrice(Number(booking.susuk_agent) || 0)}
                        </strong>
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="text-end border-0">
                        <strong
                          className={
                            Number(netKuber) < 0
                              ? "text-danger"
                              : "text-success"
                          }
                        >
                          {formatPrice(Number(netKuber) || 0)}
                        </strong>
                      </div>
                    </td>
                    <td className="text-center">{booking.total_atv}</td>
                    <td className="text-end">
                      <div className="text-end border-0">
                        {formatPrice(othersValue)}
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="d-flex flex-column gap-1">
                        {booking.type}
                        <div className="d-flex gap-1 mt-2">
                          {!booking.reservation_income_id ? (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() =>
                                handleCreateReservationIncome(booking)
                              }
                              title="Create Reservation Income"
                            >
                              <IconPlus size={14} />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() =>
                                handleUpdateReservationIncome(booking)
                              }
                              title="Update Reservation Income"
                            >
                              <IconEdit size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="small">
                        {(() => {
                          const totalMotor = Number(booking.total_atv) || 0;
                          let deduction = 0;
                          const role = (booking.type || "").toLowerCase();
                          switch (role) {
                            case "nam":
                              deduction =
                                totalMotor * 50000 + totalMotor * 50000;
                              break;
                            case "mm":
                            case "bm":
                              deduction =
                                (totalMotor * 50000 + totalMotor * 10000) - (totalMotor * 50000);
                              break;
                            case "j":
                            case "josh":
                            case "pj":
                              deduction =
                                (totalMotor * 50000 + totalMotor * 25000) - (totalMotor * 50000);
                              break;
                            default:
                              deduction = 0;
                          }
                          return (
                            <strong>{formatPrice(deduction)}</strong>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="text-end border-0">
                        <strong>
                          {formatPrice(Number(booking.net_agent) || 0)}
                        </strong>
                      </div>
                      <small className="text-muted" style={{ fontSize: "8px" }}>
                        NET AGENT = NET KUBER+OTHERS+PJ+BM/MM
                      </small>
                    </td>
                    <td>
                      {splitBillsMap[booking.code] &&
                        splitBillsMap[booking.code].length > 1 && (
                          <div className="small">
                            <strong>Split Bills:</strong>
                            {splitBillsMap[booking.code].map((split, i) => (
                              <div key={i}>
                                {split.paymentMethodName}:{" "}
                                {formatPrice(split.amount)}
                              </div>
                            ))}
                          </div>
                        )}
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-2">
                        {/* List of existing payment items */}
                        {booking.reservation_income?.payment_items?.map(
                          (item, i) => (
                            <div
                              key={i}
                              className="d-flex align-items-center justify-content-between gap-2 small p-1 border-bottom"
                            >
                              <span>
                                <strong>{item.payment_method?.name}</strong>:{" "}
                                {formatPrice(item.amount)}
                              </span>
                              <Button
                                size="sm"
                                variant="link"
                                className="text-danger p-0"
                                onClick={() =>
                                  handleRemovePaymentMethod(item.id, booking.code)
                                }
                                disabled={isSubmitting[booking.code]}
                                title="Remove Payment"
                              >
                                <IconTrash size={14} />
                              </Button>
                            </div>
                          )
                        )}
                        
                        {/* Form to add new - only if income exists */}
                        {booking.reservation_income_id ? (
                          <>
                            <Form.Select
                              size="sm"
                              value={
                                paymentInputs[booking.code]?.payment_method_id || ""
                              }
                              onChange={(e) =>
                                handlePaymentMethodSelect(
                                  booking,
                                  e.target.value
                                )
                              }
                              disabled={isSubmitting[booking.code]}
                            >
                              <option value="">Select Payment Method</option>
                              {allPaymentMethods.map((pm, i) => (
                                <option key={i} value={pm.id}>
                                  {pm.name}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control
                              id={`amount-input-${booking.code}`}
                              type="number"
                              placeholder="Amount"
                              size="sm"
                              value={paymentInputs[booking.code]?.amount || ""}
                              onChange={(e) =>
                                handlePaymentInputChange(
                                  booking.code,
                                  "amount",
                                  e.target.value
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddPaymentMethod(booking);
                                }
                              }}
                              disabled={isSubmitting[booking.code]}
                            />
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleAddPaymentMethod(booking)}
                              disabled={isSubmitting[booking.code]}
                            >
                              {isSubmitting[booking.code] ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                "Add"
                              )}
                            </Button>
                          </>
                        ) : (
                          <small className="text-muted italic">Create income first</small>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ReservationIncomeForm
        show={showReservationIncomeModal}
        onHide={handleCloseReservationIncomeModal}
        booking={selectedBooking}
        onSuccess={onRefreshData}
      />

      <BookingDetailModal 
        show={showDetailModal}
        onHide={handleCloseDetailModal}
        booking={detailBooking}
      />
    </>
  );
};

export default PaymentMethodAccordionAction;
